import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Button, Card, Badge } from 'react-bootstrap';
import { BOOK_PRODUCT_ID } from '../constants/productConfig';
import { usePricing } from '../hooks/usePricing';
import './HomeScreen.css';

function HomeScreen() {
    const { t } = useTranslation();
    const { bookCreationWithCopyPrice, additionalCopyPrice, formatPrice } = usePricing();

    const features = [
        {
            icon: '📖',
            title: t('home.featureStoryTitle'),
            text: t('home.featureStoryDesc'),
        },
        {
            icon: '🌍',
            title: t('home.featureLangTitle'),
            text: t('home.featureLangDesc'),
        },
        {
            icon: '🎵',
            title: t('home.featureToneTitle'),
            text: t('home.featureToneDesc'),
        },
        {
            icon: '📷',
            title: t('home.featurePhotoTitle'),
            text: t('home.featurePhotoDesc'),
        },
        {
            icon: '✅',
            title: t('home.featureApprovalTitle'),
            text: t('home.featureApprovalDesc'),
        },
        {
            icon: '🖨️',
            title: t('home.featurePrintTitle'),
            text: t('home.featurePrintDesc'),
        },
    ];

    const pricingRows = [
        { label: t('home.pricingBase'), price: formatPrice(bookCreationWithCopyPrice), note: t('home.pricingBaseNote'), tone: 'dark' },
        { label: t('home.pricingExtra1'), price: formatPrice(additionalCopyPrice), tone: 'dark' },
    ];

    const proofs = [
        t('home.proofRhyme'),
        t('home.proofReference'),
        t('home.proofLanguages'),
        t('home.proofPrint'),
    ];

    const heroImage = { src: '/site-assets/family-story-spread.jpg', alt: t('home.gallerySpreadAlt') };

    const galleryImages = [
        { src: '/site-assets/book-cover-flat.jpg', alt: t('home.galleryCoverAlt'), style: 'create-home__gallery-item--large' },
    ];

    const processSteps = [
        { title: t('home.processStep1Title'), text: t('home.processStep1Desc') },
        { title: t('home.processStep2Title'), text: t('home.processStep2Desc') },
        { title: t('home.processStep3Title'), text: t('home.processStep3Desc') },
    ];

    const navigate = useNavigate();
    const goToConfigurator = () => navigate(`/product/${BOOK_PRODUCT_ID}`);

    return (
        <div className="create-home">
            <section className="create-home__hero">
                <Row className="g-4 align-items-center">
                    <Col lg={6}>
                        <div className="create-home__eyebrow">{t('home.heroEyebrow')}</div>
                        <h1 className="create-home__title">{t('home.heroTitle')}</h1>
                        <p className="create-home__subtitle">{t('home.heroSubtitle')}</p>
                        <div className="create-home__proofs">
                            {proofs.map((proof) => (
                                <Badge key={proof} bg="light" text="dark" pill className="create-home__proof-badge">
                                    {proof}
                                </Badge>
                            ))}
                        </div>
                        <div className="create-home__actions">
                            <Button variant="primary" size="lg" onClick={goToConfigurator}>
                                {t('home.heroButton')}
                            </Button>
                            <Button variant="outline-dark" size="lg" onClick={() => navigate('/genai/book-guide')}>
                                {t('home.heroSecondaryButton')}
                            </Button>
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="create-home__visual-single">
                            <div className="create-home__visual create-home__visual--hero">
                                <img src={heroImage.src} alt={heroImage.alt} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </section>

            <section className="create-home__section">
                <div className="create-home__section-head">
                    <h2>{t('home.flexibilityTitle')}</h2>
                    <p>{t('home.flexibilitySubtitle')}</p>
                </div>
                <Row className="g-3">
                    {features.map((feature) => (
                        <Col key={feature.title} md={6} xl={4}>
                            <Card className="create-home__feature-card h-100 border-0 shadow-sm">
                                <Card.Body>
                                    <div className="create-home__feature-icon">{feature.icon}</div>
                                    <h3>{feature.title}</h3>
                                    <p>{feature.text}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </section>

            <section className="create-home__section">
                <Row className="g-4 align-items-start">
                    <Col lg={7}>
                        <div className="create-home__gallery-grid">
                            {galleryImages.map((image) => (
                                <figure key={image.src} className={`create-home__gallery-item ${image.style || ''}`}>
                                    <img src={image.src} alt={image.alt} />
                                </figure>
                            ))}
                        </div>
                    </Col>
                    <Col lg={5}>
                        <Card className="create-home__process-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="create-home__section-head create-home__section-head--compact">
                                    <h2>{t('home.processTitle')}</h2>
                                </div>
                                <div className="create-home__steps">
                                    {processSteps.map((step, index) => (
                                        <div key={step.title} className="create-home__step">
                                            <div className="create-home__step-number">{index + 1}</div>
                                            <div>
                                                <h3>{step.title}</h3>
                                                <p>{step.text}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>

            <section className="create-home__section">
                <Row className="g-4 align-items-stretch">
                    <Col lg={5}>
                        <Card className="create-home__pricing-card border-0 overflow-hidden shadow-sm h-100">
                            <div className="create-home__pricing-header">
                                <div className="text-uppercase small fw-semibold opacity-75">{t('home.pricingEyebrow')}</div>
                                <h3 className="mb-1 mt-2">{t('home.pricingTitle')}</h3>
                                <p className="mb-0 opacity-75">{t('home.pricingSubtitle')}</p>
                            </div>
                            <Card.Body className="p-0">
                                {pricingRows.map((row) => (
                                    <div key={row.label} className="create-home__pricing-row">
                                        <div>
                                            <div className="fw-semibold">{row.label}</div>
                                            {row.note ? <div className="small text-muted">{row.note}</div> : null}
                                        </div>
                                        <div className="fw-semibold fs-5">{row.price}</div>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={7}>
                        <Card className="create-home__guide-card border-0 shadow-sm h-100">
                            <Card.Body className="p-4 p-lg-5">
                                <div className="create-home__guide-layout">
                                    <div>
                                        <div className="create-home__section-head create-home__section-head--compact">
                                            <h2>{t('home.guideTitle')}</h2>
                                            <p>{t('home.guideSubtitle')}</p>
                                        </div>
                                        <Button variant="outline-dark" onClick={() => navigate('/genai/book-guide')}>
                                            {t('home.guideButton')}
                                        </Button>
                                    </div>
                                    <img src="/site-assets/layout-detail-open.jpg" alt="Printed sample book detail" />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>

            <section className="create-home__cta">
                <h2>{t('home.ctaTitle')}</h2>
                <Button variant="primary" size="lg" onClick={goToConfigurator}>
                    {t('home.ctaButton')}
                </Button>
            </section>
        </div>
    );
}

export default HomeScreen;